/** 键盘子应用 musicDance 页独立开关（与主应用 `music-management:musicRhythm:enabled` 无关） */
export const KEYBOARD_SUB_APP_MUSIC_RHYTHM_ENABLED_KEY =
  "keyboard-management:musicRhythm:enabled";

export const KEYBOARD_SUB_APP_MUSIC_RHYTHM_ENABLED_CHANGED_EVENT =
  "keyboard-management:musicRhythm:enabledChanged";

export type KeyboardSubAppRhythmEquipmentRef = {
  identifier?: unknown;
  uuid?: unknown;
  deviceId?: unknown;
};

/** 子应用开关按当前设备分键，避免 A 设备开过开关后进入 B 设备仍显示为开 */
export function resolveKeyboardSubAppRhythmDeviceId(
  equipment?: KeyboardSubAppRhythmEquipmentRef | null
): string | null {
  if (!equipment) return null;
  const raw =
    equipment.identifier ?? equipment.uuid ?? equipment.deviceId ?? null;
  if (raw === undefined || raw === null) return null;
  const id = String(raw).trim();
  return id.length > 0 ? id : null;
}

export function getKeyboardSubAppMusicRhythmStorageKey(
  deviceId?: string | null
): string {
  const id = deviceId?.trim();
  if (id) {
    return `${KEYBOARD_SUB_APP_MUSIC_RHYTHM_ENABLED_KEY}:${id}`;
  }
  return KEYBOARD_SUB_APP_MUSIC_RHYTHM_ENABLED_KEY;
}

export function isKeyboardSubAppMusicRhythmEnabled(
  deviceId?: string | null
): boolean {
  if (typeof window === "undefined") return false;
  try {
    const scopedKey = getKeyboardSubAppMusicRhythmStorageKey(deviceId);
    if (window.localStorage.getItem(scopedKey) === "1") {
      return true;
    }
    // 兼容旧版全局单键：仅在没有设备 id 时读取
    if (!deviceId) {
      return (
        window.localStorage.getItem(
          KEYBOARD_SUB_APP_MUSIC_RHYTHM_ENABLED_KEY
        ) === "1"
      );
    }
    return false;
  } catch {
    return false;
  }
}

/** 键盘子应用当前连接设备（与 readEquipmentJson / getCurrentDevice 一致） */
export function readKeyboardSubAppRhythmEquipmentFromStorage(): Record<
  string,
  unknown
> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      window.sessionStorage.getItem("eweadn:session:equipmentData") ??
      window.localStorage.getItem("equipmentData");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export function readCurrentKeyboardSubAppRhythmDeviceId(): string | null {
  return resolveKeyboardSubAppRhythmDeviceId(
    readKeyboardSubAppRhythmEquipmentFromStorage()
  );
}

/** 首页点设备卡片后、子应用 layout 卸载前为 true；用于挡住全局律动改写 equipmentData / 星闪 SDK */
export function isEnteringKeyboardSubAppFromCard(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("isConnecting") === "true";
  } catch {
    return false;
  }
}

export function setKeyboardSubAppMusicRhythmEnabled(
  enabled: boolean,
  deviceId?: string | null
): void {
  if (typeof window === "undefined") return;
  const value = enabled ? "1" : "0";
  try {
    const scopedKey = getKeyboardSubAppMusicRhythmStorageKey(deviceId);
    window.localStorage.setItem(scopedKey, value);
    window.dispatchEvent(
      new CustomEvent(KEYBOARD_SUB_APP_MUSIC_RHYTHM_ENABLED_CHANGED_EVENT, {
        detail: { enabled, deviceId: deviceId ?? null },
      })
    );
  } catch {
    // ignore
  }
}

export {
  supportsKeyboardSubAppMusicRhythm,
  usesSubAppV1ExclusiveVirtualKeyboard,
  type HfdRhythmEquipmentSnapshot as KeyboardSubAppMusicRhythmEquipment,
} from "./hfdWirelessMusicRhythm";
